import { createFilterOptions } from "@mui/material";
import type { AutocompleteFormComponentProps } from "components/shared/FormComponent/AutocompleteFormComponent";
import AutocompleteFormComponent, {
    defaultRenderOption,
} from "components/shared/FormComponent/AutocompleteFormComponent";
import { t } from "i18next";
import { useMemo } from "react";
import { useAppSelector } from "store/store";

interface ILocaleAutocomplete {
    label?: string;
    placeholder?: string;
    onChange?: (e, data) => void;
    control?: any;
    controlName?: string;
    /**
     * List of locales that are already selected.
     * This is used to filter out the options.
     */
    currentSelection?: string[];
    error?: any | undefined;
    infoTooltip?: { text: string; link?: string; type: "info" | "question" };
    helperText?: string;
    value?: string;
}

/**
 * Autocomplete field for choosing locales.
 * @param param0
 * @returns
 */
const LocaleAutocomplete = ({
    label,
    placeholder,
    onChange,
    control = undefined,
    controlName = undefined,
    currentSelection = [],
    error = undefined,
    infoTooltip = undefined,
    helperText = "",
    value = "",
}: ILocaleAutocomplete) => {
    const { locales } = useAppSelector((state) => state.locales);

    const options = useLocalesOptions(locales, currentSelection);

    const filterOptions = createFilterOptions({
        matchFrom: "start",
        stringify: (option: any) => t(`Locales.${option}`),
    });

    const conditionalProps: Partial<AutocompleteFormComponentProps> = {};

    if (control && controlName) {
        conditionalProps.control = control;
        conditionalProps.controlName = controlName;
    }
    if (error) conditionalProps.error = error;
    if (infoTooltip) conditionalProps.infoTooltip = infoTooltip;
    if (helperText) conditionalProps.helperText = helperText;
    if (value) conditionalProps.value = value;

    return (
        <AutocompleteFormComponent
            label={label}
            options={options}
            placeholder={placeholder}
            renderOptionText={(option: any) => t(`Locales.${option}`)}
            renderOption={(props, option, state) => defaultRenderOption(props, t(`Locales.${option}`), state)}
            isOptionEqualToValue={(option: any, value: any) => option === value}
            onChange={onChange}
            filterOptions={filterOptions}
            {...conditionalProps}
        />
    );
};

/**
 * Locales options hook
 * @param locales List of locales
 * @param currentSelection
 */
const useLocalesOptions = (locales: any[], currentSelection: string[]): string[] => {
    return useMemo(() => {
        let _locales = locales.map(({ code }) => code);
        if (currentSelection?.length > 0) {
            _locales = _locales.filter((locale) => !currentSelection.includes(locale));
        }
        return _locales;
    }, [locales, currentSelection]);
};

export default LocaleAutocomplete;
